import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const essentialCoverages = [
  {
    icon: 'ri-shield-check-line',
    title: 'General Liability (GL)',
    desc: 'Covers property damage and bodily injuries on the job. This is the foundation of any contractor policy — required by most general contractors and project owners before work begins.',
    tag: 'Required',
  },
  {
    icon: 'ri-first-aid-kit-line',
    title: 'Workers Compensation',
    desc: 'Covers employee injuries, medical bills, and lost wages. In KY, TN, and VA, workers comp is required by law once you hit the employee threshold — and construction thresholds are stricter.',
    tag: 'Often Required by Law',
  },
  {
    icon: 'ri-truck-line',
    title: 'Commercial Auto',
    desc: 'Covers work trucks, vans, and job vehicles. Personal auto insurance will not cover vehicles used for business purposes — every vehicle used on the job needs to be listed.',
    tag: 'Required for Vehicles',
  },
  {
    icon: 'ri-tools-line',
    title: 'Commercial Property / Equipment',
    desc: 'Protects tools, equipment, and buildings from fire, theft, and damage. Make sure your equipment schedule is accurate and up to date — unlisted equipment may not be covered.',
    tag: 'Essential',
  },
];

const addOns = [
  {
    icon: 'ri-umbrella-line',
    title: 'Umbrella Insurance',
    desc: 'Provides extra liability limits above your base GL and auto policies. For contractors on large projects or with significant assets, umbrella coverage is critical protection against catastrophic claims.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Inland Marine',
    desc: 'Covers tools and equipment while in transit or at job sites away from your primary location. Standard property policies often exclude equipment that\'s off-premises.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Builders Risk',
    desc: 'Covers projects while under construction — including materials, fixtures, and the structure itself. Required on most commercial construction contracts.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Pollution Liability',
    desc: 'For contractors with environmental exposure — excavation, demolition, fuel storage, or hazardous materials. Standard GL policies typically exclude pollution claims entirely.',
  },
];

const checklistItems = [
  {
    category: 'Employees & Classification',
    items: [
      'All employees are properly classified on your workers comp policy',
      'Employee count is accurate and up to date',
      'Seasonal and part-time workers are accounted for',
    ],
  },
  {
    category: 'Subcontractors',
    items: [
      'All subcontractors have their own general liability insurance',
      'All subcontractors have workers comp coverage',
      'Certificates of insurance are on file for every sub',
      'Written subcontractor agreements are in place',
    ],
  },
  {
    category: 'Equipment & Vehicles',
    items: [
      'Equipment schedule is accurate and reflects current values',
      'All work vehicles are listed and correctly rated',
      'Newly purchased equipment has been added to the policy',
      'Leased or rented equipment is covered',
    ],
  },
  {
    category: 'Coverage Limits',
    items: [
      'GL limits are high enough for the size of your projects',
      'Auto liability limits meet contract requirements',
      'Workers comp limits are appropriate for your payroll',
      'Umbrella or excess coverage is in place for large projects',
    ],
  },
  {
    category: 'Policy Review',
    items: [
      'Policy has been reviewed in the last 12 months',
      'Coverage limits have been updated as the business has grown',
      'Exclusions have been reviewed and understood',
      'Premium audit results have been reviewed for accuracy',
    ],
  },
];

export default function ContractorChecklistArticle() {
  useEffect(() => {
    document.title = 'Contractor Insurance Checklist KY TN VA | Summit Appalachia Insurance';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.setAttribute('name', 'description'); document.head.appendChild(metaDesc); }
    metaDesc.setAttribute('content', 'A complete checklist to make sure your contracting business has all the coverage it needs — general liability, workers comp, commercial auto, equipment, and more for KY, TN, VA contractors.');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/resources/contractor-insurance-checklist`);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${import.meta.env.VITE_SITE_URL}/resources/contractor-insurance-checklist#article`,
      url: `${import.meta.env.VITE_SITE_URL}/resources/contractor-insurance-checklist`,
      headline: 'Contractor Insurance Checklist: Complete Coverage Guide for KY, TN & VA',
      description: 'A complete checklist to make sure your contracting business has all the coverage it needs — general liability, workers comp, commercial auto, equipment, and more.',
      datePublished: '2025-12-01',
      dateModified: '2026-04-06',
      author: { '@type': 'Organization', name: 'Summit Appalachia Insurance' },
      publisher: {
        '@type': 'Organization',
        name: 'Summit Appalachia Insurance',
        url: import.meta.env.VITE_SITE_URL,
      },
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
      about: [
        { '@type': 'Thing', name: 'Contractor Insurance' },
        { '@type': 'Thing', name: 'General Liability Insurance' },
        { '@type': 'Thing', name: 'Workers Compensation' },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contractor-checklist';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.getElementById('schema-contractor-checklist')?.remove();
      const can = document.querySelector('link[rel="canonical"]');
      if (can) can.setAttribute('href', import.meta.env.VITE_SITE_URL ?? '/');
    };
  }, []);

  return (
    <div className="font-sans bg-[#000000]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ebb18917b7ba154491f8d3b42e4fbb4a.jpg"
            alt="Contractor Insurance Checklist"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 pb-16 w-full">
          <p className="text-white/30 text-xs font-sans mb-3 uppercase tracking-widest">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
            {' '}/ <Link to="/resources" className="hover:text-[#D4AF37] transition-colors">Resources</Link>
            {' '}/ Guide
          </p>
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-sm">Guides</span>
            <span className="text-white/30 text-xs font-sans">5 min read · Dec 2025</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-white leading-tight">
            Contractor Insurance<br />
            <span className="text-[#D4AF37]">Checklist</span>
          </h1>
          <p className="text-white/50 text-base font-sans mt-4 max-w-2xl leading-relaxed">
            A complete checklist to make sure your contracting business has all the coverage it needs — and none of the gaps that cost you everything.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-14 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="border-l-2 border-[#D4AF37] pl-6 mb-12">
            <p className="text-white/70 text-base font-sans leading-relaxed">
              If you're a contractor, one mistake or accident can cost you everything. A single uninsured job site injury, an unlisted vehicle in an accident, or a subcontractor without coverage can wipe out years of work. <strong className="text-white">This checklist helps make sure your business is properly protected and not missing critical coverage.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Essential Coverages */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">Essential Coverages</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">
            Make sure you have all four of these in place before you take on another job.
          </p>
          <div className="space-y-5">
            {essentialCoverages.map((cov, i) => (
              <div key={i} className="bg-[#111111] border border-[#D4AF37]/15 rounded-md p-6 hover:border-[#D4AF37]/35 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                  <div className="w-10 h-10 flex items-center justify-center text-[#D4AF37] flex-shrink-0">
                    <i className={`${cov.icon} text-2xl`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="font-serif text-xl text-white">{cov.title}</h3>
                      <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest font-sans border border-[#D4AF37]/40 px-2 py-0.5 rounded-sm">{cov.tag}</span>
                    </div>
                    <p className="text-white/55 text-sm font-sans leading-relaxed">{cov.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recommended Add-Ons */}
      <section className="py-16 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">Recommended Add-Ons</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">
            Depending on your operation, you may also need these coverages. Don't skip them to save money — they exist for the claims that hurt the most.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {addOns.map((addon, i) => (
              <div key={i} className="bg-[#111111] border border-[#D4AF37]/15 rounded-md p-6 hover:border-[#D4AF37]/35 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center text-[#D4AF37]">
                    <i className={`${addon.icon} text-xl`} />
                  </div>
                  <h3 className="font-sans text-sm font-bold text-white">{addon.title}</h3>
                </div>
                <p className="text-white/45 text-sm font-sans leading-relaxed">{addon.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Protection Checklist */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">Business Protection Checklist</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">
            Go through each category and make sure every item is checked off. If anything is missing, it's time to call your agent.
          </p>
          <div className="space-y-6">
            {checklistItems.map((section, si) => (
              <div key={si} className="bg-[#111111] border border-[#D4AF37]/15 rounded-md overflow-hidden">
                <div className="px-6 py-4 border-b border-[#D4AF37]/10 flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center text-[#D4AF37]">
                    <i className="ri-checkbox-circle-line text-base" />
                  </div>
                  <h3 className="font-sans text-xs font-bold text-white uppercase tracking-widest">{section.category}</h3>
                </div>
                <ul className="px-6 py-4 space-y-3">
                  {section.items.map((item, ii) => (
                    <li key={ii} className="flex items-start gap-3 text-white/55 text-sm font-sans leading-relaxed">
                      <span className="text-[#D4AF37] mt-0.5 flex-shrink-0">
                        <i className="ri-arrow-right-s-line text-base" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Tip */}
      <section className="py-14 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="bg-[#111111] border border-[#D4AF37]/30 rounded-md p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 flex items-center justify-center text-[#D4AF37]">
                <i className="ri-lightbulb-line text-xl" />
              </div>
              <h3 className="font-sans text-sm font-bold text-white uppercase tracking-widest">Final Tip</h3>
            </div>
            <p className="text-white/60 text-base font-sans leading-relaxed">
              The cheapest policy usually leaves gaps. Make sure your coverage actually protects your business — not just your budget. A single uncovered claim can cost more than years of premium savings.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0a0a0a] border-t border-[#D4AF37]/10">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Not Sure If You're Fully Covered?</h2>
          <p className="text-white/40 text-sm font-sans leading-relaxed mb-8 max-w-xl mx-auto">
            Summit Appalachia Insurance offers free policy reviews for contractors across KY, TN, VA, and all 50 states. We'll go through your current coverage and find the gaps before a claim does.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-3.5 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest rounded-md hover:bg-[#e8c84e] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get a Free Review
            </Link>
            <Link
              to="/resources"
              className="px-8 py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-bold uppercase tracking-widest rounded-md hover:border-[#D4AF37] transition-colors whitespace-nowrap cursor-pointer"
            >
              Back to Resources
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
